import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, Td, Th,
} from '@/components/erp/primitives'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { BucketAxis, ChartFrame, RankedBars, Sparkbars } from '@/shared/ui/charts'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { fmtDate, fmtDateTime, fmtNumber, fmtPercent, humanise } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import type { BenfordResponse, CostRow, MoneyResponse } from '@/shared/api/types'
import { analyticsBenford, analyticsMoney } from '../api'
import { EnvelopeBar, MixedCurrencyNote, Money, NotRun, UnattributedNote, Unavailable } from '../parts'
import { categoryLabel, channelLabel } from '../vocab'
import { AnalyticsScreen, useAnalyticsFilters } from './shell'

/**
 * The money.
 *
 * ## Reconciliation is shown whether or not it passed
 *
 * `spendTotal` is the rollup; `ledgerSum` is the sum of the ledger rows. They
 * are maintained by different code paths and they are supposed to agree. The
 * screen prints both and the difference between them, always — a reconciliation
 * that only appears when it fails is a reconciliation nobody trusts, because
 * its absence is indistinguishable from it not having run.
 *
 * ## Runway is a projection and is labelled as one
 *
 * `projectedExhaustion` is the current burn rate carried forward. It assumes
 * next month looks like last month, which for a programme that front-loads
 * disbursements is false. It is shown next to the rate it came from so the
 * assumption is visible.
 */
export function ProgramMoneyScreen() {
  return (
    <AnalyticsScreen
      title="Money"
      description="Budget against spend, the reconciliation, and where the money went."
      load={(f) => analyticsMoney(f)}
      isEmpty={(d) => (d.burnDown?.length ?? 0) === 0 && (d.costByCategory?.length ?? 0) === 0}
      exportKind="MONEY"
    >
      {(d) => <MoneyBody d={d} />}
    </AnalyticsScreen>
  )
}

function MoneyBody({ d }: { d: MoneyResponse }) {
  const cur = d.currency
  const mixed = d.currencyMixedReason
  const burn = d.burnDown ?? []
  const spent = d.spendTotal == null ? null : Number(d.spendTotal)
  const budget = d.budgetTotal == null ? null : Number(d.budgetTotal)
  // A withheld spend is not a spend of zero, so there is no percentage of budget
  // to state — `?? 0` here reported "0% of budget" for a portfolio that had spent
  // most of it in two currencies.
  const pct = spent !== null && budget && budget > 0 ? spent / budget : null

  const actual = burn.map((p) => ({
    label: fmtDate(p.date),
    value: Number(p.actual ?? 0),
    hint: p.cumulative != null ? `${fmtNumber(Number(p.cumulative))} to date` : undefined,
  }))
  const planned = burn.map((p) => ({
    label: fmtDate(p.date),
    value: Number(p.planned ?? 0),
  }))
  const hasPlan = planned.some((p) => p.value > 0)

  const discrepancy = d.discrepancy == null ? null : Number(d.discrepancy)

  return (
    <div className="space-y-4">
      <KpiStrip>
        <MetricCard
          label="Budget"
          value={<Money value={d.budgetTotal} currency={cur} mixedReason={mixed} />}
          sub={mixed
            ? 'Withheld — the selection spans more than one currency'
            : budget == null ? 'No budget set on the programmes in scope' : undefined}
        />
        <MetricCard
          label="Spent"
          value={<Money value={d.spendTotal} currency={cur} mixedReason={mixed} />}
          accent
          sub={mixed
            ? 'Withheld — the selection spans more than one currency'
            : pct == null ? 'No budget to measure against' : `${fmtPercent(pct, 0)} of budget`}
        />
        <MetricCard
          label="Burn rate"
          value={<Money value={d.burnRatePerDay} currency={cur} mixedReason={mixed} />}
          unit="/ day"
          sub={mixed
            ? 'Withheld — the selection spans more than one currency'
            : d.runwayDays == null
              ? 'No runway — spend is flat or the budget is unset'
              : `${fmtNumber(d.runwayDays)} days at this rate`}
        />
        <MetricCard
          label="Projected exhaustion"
          value={d.projectedExhaustion
            ? fmtDate(d.projectedExhaustion)
            : <Unavailable reason={mixed ?? 'There is no burn rate to project forward.'} short />}
          sub={(
            <span title="The current rate carried forward. A programme that front-loads its disbursements will beat this date and one that back-loads will not reach it.">
              assumes the rate above continues
            </span>
          )}
        />
      </KpiStrip>

      <MixedCurrencyNote reason={mixed} what="Every money figure on this screen" />
      <UnattributedNote amount={d.spendUnattributed} currency={cur} mixedReason={mixed} />

      <div
        className={cn(
          'flex items-start gap-2 rounded-md border p-3 text-erp-sm',
          d.withinTolerance
            ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200'
            : 'border-red-200 bg-red-50 text-red-900 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200',
        )}
        role={d.withinTolerance ? undefined : 'alert'}
      >
        {d.withinTolerance
          ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
          : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />}
        <div className="min-w-0">
          <div className="font-medium">
            {d.withinTolerance ? 'Rollup and ledger agree' : 'Rollup and ledger disagree'}
          </div>
          <div className="text-erp-xs">
            Rollup <Money value={d.spendTotal} currency={cur} mixedReason={mixed} />
            {' · '}ledger <Money value={d.ledgerSum} currency={cur} mixedReason={mixed} />
            {discrepancy != null && (
              <> · difference <Money value={Math.abs(discrepancy)} currency={cur} mixedReason={mixed} precise /></>
            )}
            {d.reconciledAt && <> · checked {fmtDateTime(d.reconciledAt)}</>}
          </div>
          {!d.withinTolerance && (
            <p className="mt-1 text-erp-xs">
              Treat the ledger as the record. The rollup is a cached sum and a difference
              means one of the two has missed an entry — usually a reversal that was
              recorded after the rollup ran.
            </p>
          )}
        </div>
      </div>

      <Panel
        title="Burn"
        description={hasPlan
          ? 'Two panels, one x axis. Each scales to its own peak, so compare the shapes, not the heights.'
          : 'Actual spend per day. No plan was recorded for the programmes in scope.'}
      >
        <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          <ChartFrame title="Actual" description="spend recorded per day">
            {/* A chart of zeroes is not the same picture as a chart of nothing.
                When the selection spans currencies the server sends no daily
                amounts at all, and drawing a flat baseline would say the
                programmes spent nothing on those days. */}
            {mixed
              ? <NotRun what="The daily burn" reason={mixed} />
              : (
                <>
                  <Sparkbars points={actual} emptyMessage="Nothing has been disbursed yet." />
                  <BucketAxis points={actual} />
                </>
              )}
          </ChartFrame>
          {hasPlan && !mixed && (
            <ChartFrame title="Planned" description="what the schedule said">
              <Sparkbars points={planned} />
              <BucketAxis points={planned} />
            </ChartFrame>
          )}
        </div>
      </Panel>

      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <Panel title="Cost per beneficiary by category"
          // The median went through a raw template string while every other money
          // figure on the screen went through `Money` — so under a mixed selection
          // it printed a bare number with no currency at all.
          description={mixed
            ? undefined
            : d.portfolioMedianCost != null
              ? `Portfolio median ${cur ?? ''} ${fmtNumber(Math.round(Number(d.portfolioMedianCost)))}`
              : d.medianUnavailableReason ?? undefined}>
          <CostBars
            rows={d.costByCategory}
            label={categoryLabel}
            mixed={mixed}
            empty="No category has both spend and beneficiaries."
          />
        </Panel>
        <Panel title="Cost per beneficiary by channel">
          <CostBars
            rows={d.costByChannel}
            label={channelLabel}
            mixed={mixed}
            empty="No channel has both spend and beneficiaries."
          />
        </Panel>
      </div>

      <Panel title="Spend by category" bodyClassName="p-0">
        <ErpTable head={(
          <>
            <Th>Category</Th>
            <Th className="text-right">Beneficiaries</Th>
            <Th className="text-right">Spend</Th>
            <Th className="text-right">Cost each</Th>
          </>
        )}>
          {(d.costByCategory?.length ?? 0) === 0
            ? <EmptyRow colSpan={4} message="Nothing has been disbursed under this filter." />
            : d.costByCategory.map((r) => (
              <tr key={r.key}>
                <Td>{categoryLabel(r.key)}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(r.beneficiaries)}</Td>
                <Td className="text-right"><Money value={r.spend} currency={cur} mixedReason={mixed} /></Td>
                <Td className="text-right"><Money value={r.costPerBeneficiary} currency={cur} mixedReason={mixed} /></Td>
              </tr>
            ))}
        </ErpTable>
      </Panel>

      <BenfordPanel />
    </div>
  )
}

/**
 * Cost per beneficiary, ranked.
 *
 * ## Why this refuses to draw under a mixed selection
 *
 * `CostRow` on the wire is `(key, beneficiaries, spend, costPerBeneficiary)` —
 * there is no currency on the row. Each row's figure is computed within its own
 * group, so a category whose programmes are all in shillings gets a shilling
 * figure and one whose programmes are in dollars gets a dollar figure, and
 * nothing in the payload distinguishes them.
 *
 * A bar chart compares them by LENGTH. Two bars of the same length would be a
 * claim that a dollar programme and a shilling programme cost the same, which
 * is off by a factor of a hundred and thirty. So when the selection spans
 * currencies these bars are not drawn at all. The per-row table below still
 * lists the figures, where each one stands alone rather than being measured
 * against its neighbour.
 *
 * The clean fix is a `currency` on `CostRow`, or a currency on the analytics
 * filter — neither exists yet, and this is the correct client behaviour until
 * one of them does.
 */
function CostBars({ rows, label, mixed, empty }: {
  rows?: CostRow[]
  label: (k: string) => string
  mixed?: string | null
  empty: string
}) {
  if (mixed) return <NotRun what="This comparison" reason={mixed} />
  return (
    <RankedBars
      points={(rows ?? [])
        .filter((r) => r.costPerBeneficiary != null)
        .map((r) => ({
          label: label(r.key),
          value: Number(r.costPerBeneficiary),
          hint: `${fmtNumber(r.beneficiaries)} beneficiaries`,
        }))}
      emptyMessage={empty}
    />
  )
}

/**
 * The shape of the leading digits.
 *
 * ## The copy is the feature
 *
 * PollSphere ran this over zero disbursements, computed the MAD of an empty set,
 * classified it as `suspect`, and printed "investigate potential fabrication or
 * padding" — so every programme that had not yet paid anybody was accused of
 * fraud by its own dashboard. The server now refuses to run below a sample gate
 * and sends `ran: false` with the reason, and there is no classification below
 * the gate to render.
 *
 * Above the gate, the wording says the distribution is worth checking. It never
 * says fabrication. A payments file where most amounts are a fixed grant of
 * 5,000 fails Benford by construction, and that is a normal programme.
 */
function BenfordPanel() {
  const f = useAnalyticsFilters()
  const { data, error, loading } = useLoad<BenfordResponse>(
    () => analyticsBenford(f.query), [JSON.stringify(f.query)],
  )

  if (loading && !data) return <Panel title="Do the payment amounts look natural?"><LoadingBlock label="Checking the payment amounts…" /></Panel>
  if (error) return <Panel title="Do the payment amounts look natural?"><ErrorStrip message={error} /></Panel>
  if (!data) return null

  if (!data.ran) {
    return (
      <Panel title="Do the payment amounts look natural?">
        <div className="mb-3"><EnvelopeBar env={data} /></div>
        <NotRun what="This check" reason={data.notRunReason} />
        <p className="mt-3 text-erp-xs text-muted-foreground">
          {fmtNumber(data.valueCount)} disbursement{data.valueCount === 1 ? '' : 's'} in scope.
          There is no result below the sample gate — not a passing one and not a failing one.
        </p>
      </Panel>
    )
  }

  const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9']
  const total = digits.reduce((n, k) => n + (data.observed?.[k] ?? 0), 0) || 1
  const peak = Math.max(
    ...digits.map((k) => Math.max(
      (data.observed?.[k] ?? 0) / total,
      Number(data.expected?.[k] ?? 0),
    )),
    0.01,
  )

  return (
    <Panel
      title="Do the payment amounts look natural?"
      description="In real spending, far more amounts start with a 1 than with a 9. This counts
                   the first digit of every payment and compares it to that natural pattern. A
                   bar well away from its dashed mark is worth a look — nothing more."
    >
      <div className="mb-3"><EnvelopeBar env={data} /></div>

      {/* `items-end` here left every column sized to its own content, so the bar track
          below (`flex-1`) resolved to zero height and EVERY bar rendered 0px tall with its
          expectation mark flat on the axis — a chart that silently showed nothing while the
          numbers beside it were right. The columns must stretch to the 140px; the bars sit
          on the baseline because the track itself is `items-end`. */}
      <div className="flex gap-2" style={{ height: 140 }} role="img"
        aria-label="Observed leading-digit shares against the Benford expectation.">
        {digits.map((k) => {
          const obs = (data.observed?.[k] ?? 0) / total
          const exp = Number(data.expected?.[k] ?? 0)
          return (
            <div key={k} className="flex-1 flex flex-col items-center gap-1 min-w-0">
              <div className="relative w-full flex-1 flex items-end">
                <div
                  className="w-full rounded-t-[4px]"
                  style={{ height: `${(obs / peak) * 100}%`, background: 'var(--chart-1)' }}
                  title={`Digit ${k}: ${fmtPercent(obs, 1)} observed, ${fmtPercent(exp, 1)} expected`}
                />
                {/* The expectation is a reference mark in ink, not a second
                    coloured series — there is nothing here to tell two hues apart. */}
                <div
                  className="absolute left-0 right-0 border-t-2 border-dashed border-foreground/50"
                  style={{ bottom: `${(exp / peak) * 100}%` }}
                  aria-hidden
                />
              </div>
              <span className="text-erp-xs text-muted-foreground tabular-nums">{k}</span>
            </div>
          )
        })}
      </div>
      <p className="mt-1 text-erp-xs text-muted-foreground">
        The bar is how often that digit actually starts a payment here. The dashed line is how
        often it normally would.
      </p>

      <div className="mt-3 grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <div>
          <div className="text-erp-xs uppercase tracking-wide text-muted-foreground">Values checked</div>
          <div className="text-erp-lg font-semibold tabular-nums">{fmtNumber(data.valueCount)}</div>
        </div>
        <div>
          <div className="text-erp-xs uppercase tracking-wide text-muted-foreground"
               title="Mean absolute deviation">How far off the usual pattern</div>
          <div className="text-erp-lg font-semibold tabular-nums">
            {data.mad == null ? '—' : Number(data.mad).toFixed(4)}
          </div>
        </div>
        <div>
          <div className="text-erp-xs uppercase tracking-wide text-muted-foreground">Reading</div>
          <div className="text-erp-sm">{humanise(data.classificationCode) || '—'}</div>
        </div>
      </div>

      {data.roundNumbers?.ran ? (
        <Note tone="info">
          {fmtPercent(Number(data.roundNumbers.roundShare ?? 0), 0)} of
          {' '}{fmtNumber(data.roundNumbers.valueCount)} amounts are round numbers. A programme
          that pays a fixed grant will be near 100% here and will fail the digit check as a
          matter of arithmetic. That is what a fixed grant looks like, not what fraud looks like.
        </Note>
      ) : (
        <div className="mt-3">
          <NotRun what="The round-number check" reason={data.roundNumbers?.notRunReason} />
        </div>
      )}

      <p className="mt-3 text-erp-xs text-muted-foreground">
        This is a prompt to look, and nothing more. A distribution that departs from the
        expectation is a reason to open the ledger; it is not evidence of anything, and it
        should never be repeated as an accusation about a person.
      </p>
    </Panel>
  )
}
